import ApiService from "../../utils/Api";

// IndexedDB constants
const DB_NAME = "CityGeofenceDB";
const STORE_CITIES = "cities";
const STORE_FENCES = "geofences";

const CHECK_INTERVAL = 30 * 1000;

// -----------------------------------------------------------------------------
// IndexedDB: Open DB
// -----------------------------------------------------------------------------
const openDB = () => {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      if (!db.objectStoreNames.contains(STORE_CITIES)) {
        db.createObjectStore(STORE_CITIES, { keyPath: "city_id" });
      }

      if (!db.objectStoreNames.contains(STORE_FENCES)) {
        db.createObjectStore(STORE_FENCES, { keyPath: "city_id" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject("IndexedDB failed to open");
  });
};

// -----------------------------------------------------------------------------
// IndexedDB Helpers
// -----------------------------------------------------------------------------
const idbPut = async (storeName, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([storeName], "readwrite");
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = (e) => reject(e);
  });
};

const idbGet = async (storeName, key) => {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction([storeName], "readonly");
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
};

const idbGetAll = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction([storeName], "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
};

// -----------------------------------------------------------------------------
// Load ALL cities (FULL OBJECTS)
// -----------------------------------------------------------------------------
export const loadAllCities = async () => {
  let cities = await idbGetAll(STORE_CITIES);

  // Attach geofence data
  for (let city of cities) {
    const fence = await idbGet(STORE_FENCES, city.city_id);
    city.geofence_json = fence?.geofence_json || null;
    city.redzone_geofence_json = fence?.redzone_geofence_json || "";
    city.geofence_version = fence?.geofence_version || "0";
  }
  console.log("Loaded cities from IndexedDt3ertB:", cities);

  return cities;
};

// -----------------------------------------------------------------------------
// Save ALL cities (FULL OBJECTS)
// -----------------------------------------------------------------------------
const saveAllCities = async (cities, geofenceMap) => {
  for (const city of cities) {
    // save full city object exactly as API sent it
    await idbPut(STORE_CITIES, {
      ...city,
    });

    // save geofence data
    await idbPut(STORE_FENCES, {
      city_id: city.city_id,
      geofence_json: geofenceMap[city.city_id]?.geofence_json || null,
      redzone_geofence_json:
        geofenceMap[city.city_id]?.redzone_geofence_json || "",
      geofence_version: geofenceMap[city.city_id]?.geofence_version || "0",
    });
  }
};

// -----------------------------------------------------------------------------
// MAIN: Get Cities (DB first, then API)
// -----------------------------------------------------------------------------
export const getCities = async (setcitydrop) => {
  try {
    const storedCities = await loadAllCities();

    if (storedCities.length > 0) {
      setcitydrop?.(storedCities);
      checkGeofenceVersions(setcitydrop);
      return storedCities;
    }

    return await fetchAllCities(setcitydrop);
  } catch (err) {
    console.error("Error in getCities:", err);
    return [];
  }
};

// -----------------------------------------------------------------------------
// Fetch All Cities (fresh API)
// -----------------------------------------------------------------------------
export const fetchAllCities = async (setcitydrop) => {
  try {
    const cityRes = await ApiService.post(`/commonapi/getallinone`);
    if (cityRes.status !== 200) return [];

    // FULL city objects (NO FILTERING)
    let cities = cityRes.data.response.City || [];

    const geoRes = await ApiService.post(`/commonapi/getgeofences`);
    const geoMap = geoRes.data.response || {};

    // Save to DB
    await saveAllCities(cities, geoMap);

    // Prepare final objects for UI
    const final = cities.map((city) => ({
      ...city,
      geofence_json: geoMap[city.city_id]?.geofence_json || null,
      redzone_geofence_json:
        geoMap[city.city_id]?.redzone_geofence_json || "",
      geofence_version: geoMap[city.city_id]?.geofence_version || "0",
    }));

    console.log("Fetched cities from API:", final);

    setcitydrop?.(final);
    return final;
  } catch (err) {
    console.error("Error fetching cities:", err);
    return [];
  }
};

// -----------------------------------------------------------------------------
// Version Checker
// -----------------------------------------------------------------------------
export const checkGeofenceVersions = async (setcitydrop) => {
  try {
    const storedCities = await loadAllCities();
    if (!storedCities.length) return;

    const cityRes = await ApiService.post(`/commonapi/getallinone`);
    const latestCities = cityRes.data.response.City || [];

    const geoRes = await ApiService.post(`/commonapi/getgeofences`);
    const geoMap = geoRes.data.response || {};

    let updated = false;

    for (const city of latestCities) {
      const stored = storedCities.find((c) => c.city_id === city.city_id);
      const newVer = geoMap[city.city_id]?.geofence_version || "0";

      if (!stored || stored.geofence_version < newVer) {
        updated = true;

        // Save updated city object
        await idbPut(STORE_CITIES, { ...city });

        await idbPut(STORE_FENCES, {
          city_id: city.city_id,
          geofence_json: geoMap[city.city_id]?.geofence_json || null,
          redzone_geofence_json:
            geoMap[city.city_id]?.redzone_geofence_json || "",
          geofence_version: newVer,
        });
      }
    }

    if (updated) {
      const final = await loadAllCities();
      setcitydrop?.(final);
    }
  } catch (err) {
    console.error("Error checking geofence versions:", err);
  }
};

// -----------------------------------------------------------------------------
// Start Auto Version Checker
// -----------------------------------------------------------------------------
export const startGeofenceVersionCheck = (setcitydrop) => {
  checkGeofenceVersions(setcitydrop);

  const interval = setInterval(() => {
    checkGeofenceVersions(setcitydrop);
  }, CHECK_INTERVAL);

  return () => clearInterval(interval);
};
